import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ReactMarkdown from 'react-markdown';
import blogPosts from '../data/blogPosts';

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4" style={{ color: '#20B2AA' }}>
            Blog Post Not Found
          </h1>
          <Link to="/blog" className="text-[#FF69B4] hover:underline">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20">
      <Helmet>
        <title>{post.title} | Strawberry Island Blog</title>
        <meta name="description" content={post.metaDescription} />
        <meta name="keywords" content={post.keywords} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={post.image} />
        <meta property="og:type" content="article" />
      </Helmet>

      {/* Hero Image */}
      <section className="relative h-[60vh] overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
          <div className="max-w-4xl mx-auto">
            <div className="inline-block bg-[#FF69B4] px-4 py-2 rounded-full text-sm font-semibold mb-4">
              {post.category}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 brand-script">
              {post.title}
            </h1>
            <div className="flex items-center text-sm">
              <span>{post.author}</span>
              <span className="mx-3">•</span>
              <span>{post.date}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <article className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <ReactMarkdown
              components={{
                h1: ({node, ...props}) => <h1 className="text-4xl font-bold mb-6" style={{ color: '#20B2AA' }} {...props} />,
                h2: ({node, ...props}) => <h2 className="text-3xl font-bold mt-8 mb-4" style={{ color: '#20B2AA' }} {...props} />,
                h3: ({node, ...props}) => <h3 className="text-2xl font-bold mt-6 mb-3" style={{ color: '#FF69B4' }} {...props} />,
                p: ({node, ...props}) => <p className="text-gray-700 text-lg leading-relaxed mb-4" {...props} />,
                ul: ({node, ...props}) => <ul className="list-disc list-inside mb-4 space-y-2" {...props} />,
                li: ({node, ...props}) => <li className="text-gray-700 text-lg" {...props} />,
                a: ({node, ...props}) => <a className="text-[#20B2AA] hover:text-[#FF69B4] underline" {...props} />,
                strong: ({node, ...props}) => <strong className="font-bold" style={{ color: '#FF69B4' }} {...props} />,
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>

          {/* Contact CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-[#20B2AA]/10 to-[#FF69B4]/10 rounded-2xl">
            <h3 className="text-2xl font-bold mb-4" style={{ color: '#20B2AA' }}>
              Ready to Book Your Adventure?
            </h3>
            <p className="text-gray-700 text-lg mb-6">
              Contact Strawberry Island today to plan your perfect Tofino experience!
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="mailto:joel@fishingtofino.net"
                className="inline-block bg-gradient-to-r from-[#20B2AA] to-[#5DADE2] text-white px-8 py-4 rounded-full text-lg font-semibold hover:scale-105 transition-transform text-center"
              >
                Email Us 📧
              </a>
              <a
                href="tel:+12503170600"
                className="inline-block bg-gradient-to-r from-[#FF69B4] to-[#FFB6C1] text-white px-8 py-4 rounded-full text-lg font-semibold hover:scale-105 transition-transform text-center"
              >
                Call Now 📞
              </a>
            </div>
          </div>

          {/* Back to Blog */}
          <div className="mt-12 text-center">
            <Link
              to="/blog"
              className="inline-block text-[#20B2AA] hover:text-[#FF69B4] font-semibold text-lg transition-colors"
            >
              ← Back to All Blog Posts
            </Link>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#20B2AA' }}>
            More From Our Blog
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {blogPosts
              .filter((p) => p.id !== post.id)
              .slice(0, 3)
              .map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                >
                  <img
                    src={relatedPost.image}
                    alt={relatedPost.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-2 hover:text-[#20B2AA] transition-colors">
                      {relatedPost.title}
                    </h3>
                    <p className="text-gray-600 text-sm line-clamp-2">
                      {relatedPost.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPostPage;
